<template>
  <div
    class="two-columns-widget widget-container"
    :class="{ 'widget-editable': isEditable }"
    :style="widgetStyles"
  >
    <div
      class="columns-container"
      :class="columnContainerClasses"
      :style="[columnContainerStyle, activeColumnsGridStyle]"
    >
      <!-- Left Column -->
      <div
        class="column left-column"
        :class="{ 'column-empty': widget.leftColumn.length === 0 }"
      >
        <div class="column-header" v-if="isEditable">
          <small class="text-muted">
            <i class="bi bi-layout-sidebar-inset me-1"></i>
            Αριστερή στήλη
          </small>
        </div>

        <div
          class="column-content"
          :class="{ 'drag-area': isEditable }"
          :style="{ justifyContent: alignmentClass }"
        >
          <!-- Admin Mode: Show controls and editable widgets -->
          <template v-if="isEditable">
            <div
              v-for="(nestedWidget, index) in leftColumnWidgets"
              :key="nestedWidget.id"
              class="nested-widget"
            >
              <div v-if="!modalOpen" class="widget-controls">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-1"
                  @click="editNestedWidget(nestedWidget)"
                  title="Επεξεργασία"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger me-1"
                  @mousedown.stop
                  @pointerdown.stop
                  @click.stop="removeNestedWidget('left', index)"
                  title="Διαγραφή"
                >
                  <i class="bi bi-trash"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary me-1"
                  @click="moveNestedWidget('left', index, 'up')"
                  :disabled="index === 0"
                  title="Μετακίνηση Επάνω"
                >
                  <i class="bi bi-arrow-up"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="moveNestedWidget('left', index, 'down')"
                  :disabled="index === leftColumnWidgets.length - 1"
                  title="Μετακίνηση Κάτω"
                >
                  <i class="bi bi-arrow-down"></i>
                </button>
              </div>

              <component
                :is="getWidgetComponent(nestedWidget.type)"
                :widget="nestedWidget"
                :is-editable="false"
                @update="updateNestedWidget('left', index, $event)"
              />
            </div>
            <div class="add-widget-area">
              <button
                type="button"
                class="btn btn-outline-primary add-widget-btn w-100"
                @click="showAddWidget('left')"
              >
                <i class="bi bi-plus-circle me-2"></i>
                Προσθήκη Widget
              </button>
            </div>
          </template>

          <!-- Public Mode: Show only content without controls -->
          <template v-else>
            <div
              v-for="nestedWidget in leftColumnWidgets"
              :key="nestedWidget.id"
              class="nested-widget-public"
            >
              <component
                :is="getWidgetComponent(nestedWidget.type)"
                :widget="nestedWidget"
                :is-editable="false"
              />
            </div>
          </template>
        </div>
      </div>

      <!-- Right Column -->
      <div
        class="column right-column"
        :class="{ 'column-empty': widget.rightColumn.length === 0 }"
      >
        <div class="column-header" v-if="isEditable">
          <small class="text-muted">
            <i class="bi bi-layout-sidebar-inset-reverse me-1"></i>
            Δεξιά στήλη
          </small>
        </div>

        <div
          class="column-content"
          :class="{ 'drag-area': isEditable }"
          :style="{ justifyContent: alignmentClass }"
        >
          <!-- Admin Mode: Show controls and editable widgets -->
          <template v-if="isEditable">
            <div
              v-for="(nestedWidget, index) in rightColumnWidgets"
              :key="nestedWidget.id"
              class="nested-widget"
            >
              <div v-if="!modalOpen" class="widget-controls">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-1"
                  @click="editNestedWidget(nestedWidget)"
                  title="Επεξεργασία"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger me-1"
                  @mousedown.stop
                  @pointerdown.stop
                  @click.stop="removeNestedWidget('right', index)"
                  title="Διαγραφή"
                >
                  <i class="bi bi-trash"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary me-1"
                  @click="moveNestedWidget('right', index, 'up')"
                  :disabled="index === 0"
                  title="Μετακίνηση Επάνω"
                >
                  <i class="bi bi-arrow-up"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="moveNestedWidget('right', index, 'down')"
                  :disabled="index === rightColumnWidgets.length - 1"
                  title="Μετακίνηση Κάτω"
                >
                  <i class="bi bi-arrow-down"></i>
                </button>
              </div>

              <component
                :is="getWidgetComponent(nestedWidget.type)"
                :widget="nestedWidget"
                :is-editable="false"
                @update="updateNestedWidget('right', index, $event)"
              />
            </div>
            <div class="add-widget-area">
              <button
                type="button"
                class="btn btn-outline-primary add-widget-btn w-100"
                @click="showAddWidget('right')"
              >
                <i class="bi bi-plus-circle me-2"></i>
                Προσθήκη Widget
              </button>
            </div>
          </template>

          <!-- Public Mode: Show only content without controls -->
          <template v-else>
            <div
              v-for="nestedWidget in rightColumnWidgets"
              :key="nestedWidget.id"
              class="nested-widget-public"
            >
              <component
                :is="getWidgetComponent(nestedWidget.type)"
                :widget="nestedWidget"
                :is-editable="false"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Layout Settings (shown when widget is selected) -->
    <div v-if="isEditable" class="widget-settings mt-3">
      <div class="row g-3">
        <div class="col-md-4">
          <label class="form-label">Αναλογία Στηλών</label>
          <select
            v-model="widget.columnRatio"
            class="form-select form-select-sm"
            @change="onLayoutChange"
          >
            <option value="1-1">50% - 50%</option>
            <option value="1-2">33% - 67%</option>
            <option value="2-1">67% - 33%</option>
            <option value="1-3">25% - 75%</option>
            <option value="3-1">75% - 25%</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Κατακόρυφη Στοίχιση</label>
          <select
            v-model="widget.verticalAlign"
            class="form-select form-select-sm"
            @change="onLayoutChange"
          >
            <option value="top">Πάνω</option>
            <option value="center">Κέντρο</option>
            <option value="bottom">Κάτω</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Απόσταση Στηλών</label>
          <select v-model="widget.gap" class="form-select form-select-sm" @change="onLayoutChange">
            <option value="0.5rem">Μικρή</option>
            <option value="1rem">Μεσαία</option>
            <option value="2rem">Μεγάλη</option>
            <option value="3rem">Πολύ Μεγάλη</option>
          </select>
        </div>
        <div class="col-12">
          <label class="form-label">Διάταξη στο κινητό</label>
          <select
            v-model="widget.mobileLayout"
            class="form-select form-select-sm"
            @change="onLayoutChange"
          >
            <option
              v-for="option in MOBILE_COLUMN_LAYOUT_OPTIONS[2]"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </div>

  <!-- Widget Selection Modal -->
  <div v-if="showWidgetSelector" class="custom-widget-selector-modal" @click="closeWidgetSelector">
    <div class="custom-modal-dialog" @click.stop>
      <div class="custom-modal-content">
        <div class="custom-modal-header">
          <h5 class="custom-modal-title">
            <i class="bi bi-grid-3x3-gap me-2"></i>
            Επιλογή Widget
          </h5>
          <button type="button" class="custom-close-btn" @click="closeWidgetSelector">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div class="custom-modal-body">
          <div class="container-fluid">
            <div class="row g-3">
              <!-- Basic Widgets -->
              <div class="col-12">
                <h6 class="text-muted mb-2">Βασικά Widget</h6>
                <div class="row g-2">
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-primary w-100 p-3"
                      @click="addWidgetToColumn('text')"
                    >
                      <i class="bi bi-text-paragraph d-block mb-1"></i>
                      <small>Κείμενο</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-warning w-100 p-3"
                      @click="addWidgetToColumn('enhanced-text')"
                    >
                      <i class="bi bi-stars d-block mb-1"></i>
                      <small>Enhanced Text</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-primary w-100 p-3"
                      @click="addWidgetToColumn('image')"
                    >
                      <i class="bi bi-image d-block mb-1"></i>
                      <small>Εικόνα</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-primary w-100 p-3"
                      @click="addWidgetToColumn('button')"
                    >
                      <i class="bi bi-square d-block mb-1"></i>
                      <small>Κουμπί</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-secondary w-100 p-3"
                      @click="addWidgetToColumn('video')"
                    >
                      <i class="bi bi-play-circle d-block mb-1"></i>
                      <small>Βίντεο</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-secondary w-100 p-3"
                      @click="addWidgetToColumn('spacer')"
                    >
                      <i class="bi bi-arrows-expand-vertical d-block mb-1"></i>
                      <small>Κενό</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-secondary w-100 p-3"
                      @click="addWidgetToColumn('divider')"
                    >
                      <i class="bi bi-hr d-block mb-1"></i>
                      <small>Διαχωριστικό</small>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Interactive Widgets -->
              <div class="col-12">
                <h6 class="text-muted mb-2">Διαδραστικά</h6>
                <div class="row g-2">
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-success w-100 p-3"
                      @click="addWidgetToColumn('contact-form')"
                    >
                      <i class="bi bi-envelope d-block mb-1"></i>
                      <small>Φόρμα Επικοινωνίας</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-success w-100 p-3"
                      @click="addWidgetToColumn('accordion')"
                    >
                      <i class="bi bi-list d-block mb-1"></i>
                      <small>Accordion</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-success w-100 p-3"
                      @click="addWidgetToColumn('tabs')"
                    >
                      <i class="bi bi-folder-plus d-block mb-1"></i>
                      <small>Tabs</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-success w-100 p-3"
                      @click="addWidgetToColumn('progress-bar')"
                    >
                      <i class="bi bi-bar-chart d-block mb-1"></i>
                      <small>Progress Bar</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-success w-100 p-3"
                      @click="addWidgetToColumn('social-media')"
                    >
                      <i class="bi bi-share d-block mb-1"></i>
                      <small>Social Media</small>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Content Widgets -->
              <div class="col-12">
                <h6 class="text-muted mb-2">Περιεχόμενο</h6>
                <div class="row g-2">
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-info w-100 p-3"
                      @click="addWidgetToColumn('post-cards')"
                    >
                      <i class="bi bi-newspaper d-block mb-1"></i>
                      <small>Κάρτες Άρθρων</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-info w-100 p-3"
                      @click="addWidgetToColumn('product-cards')"
                    >
                      <i class="bi bi-shop d-block mb-1"></i>
                      <small>Κάρτες Προϊόντων</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-info w-100 p-3"
                      @click="addWidgetToColumn('tour-cards')"
                    >
                      <i class="bi bi-bus-front d-block mb-1"></i>
                      <small>Κάρτες Εκδρομών</small>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Multimedia -->
              <div class="col-12">
                <h6 class="text-muted mb-2">Multimedia</h6>
                <div class="row g-2">
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-secondary w-100 p-3"
                      @click="addWidgetToColumn('gallery')"
                    >
                      <i class="bi bi-images d-block mb-1"></i>
                      <small>Γκαλερί</small>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Visualization & Other -->
              <div class="col-12">
                <h6 class="text-muted mb-2">Οπτικοποίηση & Άλλα</h6>
                <div class="row g-2">
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-warning w-100 p-3"
                      @click="addWidgetToColumn('counter')"
                    >
                      <i class="bi bi-speedometer2 d-block mb-1"></i>
                      <small>Μετρητές</small>
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-dark w-100 p-3"
                      @click="addWidgetToColumn('html')"
                    >
                      <i class="bi bi-code d-block mb-1"></i>
                      <small>HTML</small>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeWidgetSelector">
            Ακύρωση
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { TwoColumnsWidget, SimpleWidget, Widget } from '@/types/pagebuilder'

// Import widgets for dynamic components
import TextWidget from './TextWidget.vue'
import EnhancedTextWidget from './EnhancedTextWidget.vue'
import ButtonWidget from './ButtonWidget.vue'
import ImageWidget from './ImageWidget.vue'
import SpacerWidget from './SpacerWidget.vue'
import DividerWidget from './DividerWidget.vue'
import VideoWidget from './VideoWidget.vue'
import PostCardsWidget from './PostCardsWidget.vue'
import ProductCardsWidget from './ProductCardsWidget.vue'
import ContactFormWidget from './ContactFormWidget.vue'
import AccordionWidget from './AccordionWidget.vue'
import TabsWidget from './TabsWidget.vue'
import ProgressBarWidget from './ProgressBarWidget.vue'
import CounterWidget from './CounterWidget.vue'
import SocialMediaWidget from './SocialMediaWidget.vue'
import GalleryWidget from './GalleryWidget.vue'
import TourCardsWidget from './TourCardsWidget.vue'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'
import {
  getColumnContainerClasses,
  getColumnContainerStyle,
  getActiveColumnsGridStyle,
  MOBILE_COLUMN_LAYOUT_OPTIONS,
} from '@/utils/columnLayout'
import HtmlWidget from './HtmlWidget.vue'

interface Props {
  widget: TwoColumnsWidget
  isEditable?: boolean
  modalOpen?: boolean
}

interface Emits {
  (e: 'update', widget: TwoColumnsWidget): void
  (e: 'edit-child', widget: Widget): void
}

const props = withDefaults(defineProps<Props>(), {
  isEditable: false,
  modalOpen: false,
})

const emit = defineEmits<Emits>()

// State
const showWidgetSelector = ref(false)
const selectedColumn = ref<'left' | 'right'>('left')
const editingWidget = ref<SimpleWidget | null>(null)

// Computed
const widgetStyles = computed(() => ({
  ...resolveWidgetBackgroundStyle(props.widget),
  padding: props.widget.padding || '1rem',
}))

const columnRatioClass = computed(() => {
  switch (props.widget.columnRatio) {
    case '1-2':
      return 'ratio-1-2'
    case '2-1':
      return 'ratio-2-1'
    case '1-3':
      return 'ratio-1-3'
    case '3-1':
      return 'ratio-3-1'
    default:
      return 'ratio-1-1'
  }
})

const columnContainerClasses = computed(() =>
  getColumnContainerClasses(2, props.widget.mobileLayout, columnRatioClass.value)
)

const columnContainerStyle = computed(() =>
  getColumnContainerStyle(2, props.widget.mobileLayout, props.widget.gap)
)

const activeColumnsGridStyle = computed(() =>
  getActiveColumnsGridStyle(
    [leftColumnWidgets.value.length, rightColumnWidgets.value.length],
    !!props.isEditable,
  ),
)

const alignmentClass = computed(() => {
  switch (props.widget.verticalAlign) {
    case 'center':
      return 'center'
    case 'bottom':
      return 'flex-end'
    default:
      return 'flex-start'
  }
})

const leftColumnWidgets = computed({
  get: () => props.widget.leftColumn || [],
  set: (value) => updateWidget({ ...props.widget, leftColumn: value }),
})

const rightColumnWidgets = computed({
  get: () => props.widget.rightColumn || [],
  set: (value) => updateWidget({ ...props.widget, rightColumn: value }),
})

// Methods
const updateWidget = (updatedWidget: TwoColumnsWidget) => {
  emit('update', updatedWidget)
}

const onLayoutChange = () => {
  updateWidget(props.widget)
}

const showAddWidget = (column: 'left' | 'right') => {
  selectedColumn.value = column
  showWidgetSelector.value = true
}

const closeWidgetSelector = () => {
  showWidgetSelector.value = false
}

const addWidgetToColumn = (widgetType: string) => {
  const newWidget: Partial<SimpleWidget> = {
    id: `${Date.now()}-${Math.random()}`,
    type: widgetType as any,
    order: 0,
  }

  // Add default props based on widget type
  switch (widgetType) {
    case 'text':
      Object.assign(newWidget, {
        content: '<p>Νέο κείμενο...</p>',
        textAlign: 'left',
      })
      break
    case 'enhanced-text':
      Object.assign(newWidget, {
        title: 'Νέος Τίτλος',
        content: '<p>Περιγραφή...</p>',
        icon: 'bi bi-star',
        iconColor: '#007bff',
        style: 'card',
        textAlign: 'left',
      })
      break
    case 'button':
      Object.assign(newWidget, {
        text: 'Κλικ εδώ',
        url: '#',
        buttonStyle: 'primary',
      })
      break
    case 'image':
      Object.assign(newWidget, {
        imageUrl: '',
        altText: 'Εικόνα',
        alignment: 'center',
      })
      break
    case 'contact-form':
      Object.assign(newWidget, {
        title: 'Επικοινωνήστε μαζί μας',
        showName: true,
        showPhone: false,
        showSubject: false,
      })
      break
    case 'accordion':
      Object.assign(newWidget, {
        title: 'Συχνές Ερωτήσεις',
        items: [
          { id: '1', title: 'Ερώτηση 1', content: 'Απάντηση 1', isOpen: false },
          { id: '2', title: 'Ερώτηση 2', content: 'Απάντηση 2', isOpen: false },
        ],
      })
      break
    case 'gallery':
      Object.assign(newWidget, {
        images: [],
        columns: 3,
        spacing: '1rem',
        lightbox: true,
        styleMode: 'simple',
        showTitles: false,
        imageBorderRadius: '8px',
        imageHeight: '200px',
      })
      break
    case 'counter':
      Object.assign(newWidget, {
        title: 'Τα Επιτεύγματά μας',
        counters: [
          { id: 'counter-1', number: 100, label: 'Πελάτες', suffix: '+', prefix: '', icon: 'bi bi-people', color: '#007bff' },
        ],
        columns: 2,
        animated: true,
        duration: 2000,
        style: 'default',
      })
      break
    case 'tour-cards':
      Object.assign(newWidget, {
        categoryId: '',
        limit: 3,
        columns: 2,
        showPrice: true,
        showDuration: true,
        showImage: true,
        cardStyle: 'default',
        title: '',
        showBookButton: true,
      })
      break
    case 'html':
      Object.assign(newWidget, {
        htmlContent: '<div>Προσαρμοσμένος HTML κώδικας</div>',
      })
      break
    // Add more default props for other widgets...
  }

  if (selectedColumn.value === 'left') {
    const leftColumn = [...(props.widget.leftColumn || []), newWidget as SimpleWidget]
    updateWidget({ ...props.widget, leftColumn })
  } else {
    const rightColumn = [...(props.widget.rightColumn || []), newWidget as SimpleWidget]
    updateWidget({ ...props.widget, rightColumn })
  }

  closeWidgetSelector()
}

const updateNestedWidget = (
  column: 'left' | 'right',
  index: number,
  updatedWidget: SimpleWidget,
) => {
  if (column === 'left') {
    const leftColumn = [...leftColumnWidgets.value]
    leftColumn[index] = updatedWidget
    updateWidget({ ...props.widget, leftColumn })
  } else {
    const rightColumn = [...rightColumnWidgets.value]
    rightColumn[index] = updatedWidget
    updateWidget({ ...props.widget, rightColumn })
  }
}

const removeNestedWidget = (column: 'left' | 'right', index: number) => {
  if (column === 'left') {
    const leftColumn = leftColumnWidgets.value.filter((_, i) => i !== index)
    updateWidget({ ...props.widget, leftColumn })
  } else {
    const rightColumn = rightColumnWidgets.value.filter((_, i) => i !== index)
    updateWidget({ ...props.widget, rightColumn })
  }
}

const moveNestedWidget = (column: 'left' | 'right', index: number, direction: 'up' | 'down') => {
  const widgets = column === 'left' ? [...leftColumnWidgets.value] : [...rightColumnWidgets.value]
  const newIndex = direction === 'up' ? index - 1 : index + 1

  if (newIndex >= 0 && newIndex < widgets.length) {
    const widget = widgets.splice(index, 1)[0]
    widgets.splice(newIndex, 0, widget)

    if (column === 'left') {
      updateWidget({ ...props.widget, leftColumn: widgets })
    } else {
      updateWidget({ ...props.widget, rightColumn: widgets })
    }
  }
}

const editNestedWidget = (widget: SimpleWidget) => {
  editingWidget.value = widget
  emit('edit-child', widget as Widget)
}

const onWidgetSave = (updatedWidget: Widget) => {
  // Find and update the widget in the appropriate column
  const leftIndex = leftColumnWidgets.value.findIndex((w) => w.id === updatedWidget.id)
  if (leftIndex !== -1) {
    updateNestedWidget('left', leftIndex, updatedWidget as SimpleWidget)
  } else {
    const rightIndex = rightColumnWidgets.value.findIndex((w) => w.id === updatedWidget.id)
    if (rightIndex !== -1) {
      updateNestedWidget('right', rightIndex, updatedWidget as SimpleWidget)
    }
  }
  editingWidget.value = null
}

const onWidgetCancel = () => {
  editingWidget.value = null
}

const getWidgetComponent = (type: string) => {
  const components: Record<string, any> = {
    text: TextWidget,
    'enhanced-text': EnhancedTextWidget,
    button: ButtonWidget,
    image: ImageWidget,
    spacer: SpacerWidget,
    divider: DividerWidget,
    video: VideoWidget,
    'post-cards': PostCardsWidget,
    'product-cards': ProductCardsWidget,
    'contact-form': ContactFormWidget,
    accordion: AccordionWidget,
    tabs: TabsWidget,
    'progress-bar': ProgressBarWidget,
    counter: CounterWidget,
    'social-media': SocialMediaWidget,
    gallery: GalleryWidget,
    'tour-cards': TourCardsWidget,
    html: HtmlWidget,
  }

  return components[type] || 'div'
}

// Expose methods for parent component
defineExpose({
  onWidgetSave,
  onWidgetCancel,
})
</script>

<style scoped>
.two-columns-widget {
  min-height: 100px;
}

.columns-container {
  display: grid;
  gap: 1rem;
  min-height: 200px;
}

.ratio-1-1 {
  grid-template-columns: 1fr 1fr;
}
.ratio-1-2 {
  grid-template-columns: 1fr 2fr;
}
.ratio-2-1 {
  grid-template-columns: 2fr 1fr;
}
.ratio-1-3 {
  grid-template-columns: 1fr 3fr;
}
.ratio-3-1 {
  grid-template-columns: 3fr 1fr;
}

/* Override ratios on mobile - always stack */
@media (max-width: 768px) {
  .ratio-1-1,
  .ratio-1-2,
  .ratio-2-1,
  .ratio-1-3,
  .ratio-3-1 {
    grid-template-columns: 1fr !important;
  }
}

.column {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 150px;
  min-width: 0;
  position: relative;
}

.column-header {
  padding: 0.5rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  width: 100%;
}

.column-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 2px dashed transparent;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.column-empty .column-content {
  border-color: #dee2e6;
  background: #fafafa;
}

.two-columns-widget:not(.widget-editable) .column-empty {
  display: none;
}

.nested-widget :deep(.enhanced-text-widget.has-background-image),
.nested-widget-public :deep(.enhanced-text-widget.has-background-image) {
  width: 100%;
  max-width: 100%;
  align-self: stretch;
}

.nested-widget :deep(.enhanced-text-widget.has-background-image .background-image-layer),
.nested-widget-public :deep(.enhanced-text-widget.has-background-image .background-image-layer) {
  width: 100% !important;
  height: 100% !important;
  max-height: none !important;
  object-fit: cover !important;
}

.drag-area:empty {
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-area:empty::before {
  content: 'Σύρετε widgets εδώ';
  color: #6c757d;
  font-style: italic;
}

.nested-widget {
  position: relative;
  margin-bottom: 1rem;
  overflow: visible;
  min-height: 60px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

/* Edit mode styles - only show borders when editable */
.two-columns-widget.widget-editable .nested-widget {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

/* Admin preview: Keep images as thumbnails */
.two-columns-widget.widget-editable .nested-widget :deep(.image-widget img),
.two-columns-widget.widget-editable .nested-widget :deep(.widget-image) {
  max-width: 100%;
  max-height: 200px;
  width: auto !important;
  height: auto !important;
  object-fit: contain;
}

/* Admin preview: Image container constraints */
.two-columns-widget.widget-editable .nested-widget :deep(.image-widget),
.two-columns-widget.widget-editable .nested-widget :deep(.image-container) {
  max-width: 100%;
  display: block;
}

.nested-widget:last-child {
  margin-bottom: 0;
}

.nested-widget-public {
  margin-bottom: 1rem;
  min-width: 0;
  max-width: 100%;
}

.nested-widget-public :deep(.gallery-widget.mobile-layout-carousel) {
  width: 100%;
  min-width: 0;
}

.nested-widget-public:last-child {
  margin-bottom: 0;
}

.nested-widget .widget-controls {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 1;
  transition: opacity 0.3s ease;
  z-index: 99999;
  background: rgba(255, 255, 255, 0.98);
  padding: 6px;
  border-radius: 6px;
  backdrop-filter: blur(4px);
  pointer-events: auto;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  gap: 4px;
  align-items: center;
  width: auto;
  min-width: 120px;
}

.nested-widget .widget-controls .btn {
  pointer-events: auto;
  z-index: 100000;
  position: relative;
  min-width: 32px;
  min-height: 32px;
  padding: 6px;
  font-size: 12px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex-shrink: 0;
}

.nested-widget .widget-controls .btn:hover {
  transform: scale(1.05);
  transition: transform 0.2s ease;
}

.nested-widget:hover .widget-controls {
  opacity: 1;
}

.add-widget-area {
  margin-top: 1rem;
}

.add-widget-btn {
  border: 2px dashed #007bff !important;
  background: transparent !important;
  color: #007bff !important;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1000;
  pointer-events: auto !important;
}

.add-widget-btn:hover {
  background: #007bff !important;
  color: white !important;
  border-color: #007bff !important;
}

.add-widget-btn i {
  font-size: 1.2rem;
}

/* Widget Settings */
.widget-settings {
  position: static;
  width: 100%;
  margin-top: 1rem;
}

/* Custom Modal Styles */
.custom-widget-selector-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 15000;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    background: rgba(0, 0, 0, 0);
  }
  to {
    opacity: 1;
    background: rgba(0, 0, 0, 0.7);
  }
}

.custom-widget-selector-modal .custom-modal-dialog {
  width: 90vw;
  max-width: 700px;
  max-height: 85vh;
  pointer-events: auto;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    transform: scale(0.9) translateY(-20px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.custom-modal-dialog {
  margin: 0;
  pointer-events: auto;
}

.custom-modal-content {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 12px;
  box-shadow:
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 10px 20px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  pointer-events: auto;
  overflow: hidden;
}

.custom-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.75rem;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border-bottom: none;
}

.custom-modal-title {
  margin: 0;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
}

.custom-modal-body {
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
  background: #f8f9fa;
}

.custom-modal-body::-webkit-scrollbar {
  width: 6px;
}

.custom-modal-body::-webkit-scrollbar-track {
  background: #e9ecef;
  border-radius: 3px;
}

.custom-modal-body::-webkit-scrollbar-thumb {
  background: #3b82f6;
  border-radius: 3px;
}

.custom-modal-body::-webkit-scrollbar-thumb:hover {
  background: #1d4ed8;
}

.widget-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  padding: 0;
}

.widget-item {
  background: white;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.widget-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.widget-item:hover::before {
  transform: scaleX(1);
}

.widget-item:hover {
  border-color: #3b82f6;
  transform: translateY(-2px);
  box-shadow:
    0 8px 25px rgba(59, 130, 246, 0.15),
    0 3px 10px rgba(0, 0, 0, 0.1);
}

.widget-item h5 {
  margin: 0 0 0.5rem 0;
  color: #1f2937;
  font-weight: 600;
  font-size: 1rem;
}

.widget-item p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
}

.widget-item .badge {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-weight: 500;
}

.btn-close {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.btn-close::before {
  content: '×';
  font-size: 20px;
  color: white;
  font-weight: bold;
}

.custom-close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.custom-close-btn:hover {
  background: #f8f9fa;
  color: #007bff;
}

.custom-modal-body {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}

.custom-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

@media (max-width: 767.98px) {
  .widget-settings {
    position: static;
    width: 100%;
    margin-top: 1rem;
  }

  .custom-modal-body {
    padding: 1rem !important;
  }
}
</style>
